import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtFecha(val) {
    if (!val) return '—';
    const digits = String(val).replace(/\D/g, '');
    if (digits.length === 8) {
        return `${digits.slice(6, 8)}/${digits.slice(4, 6)}/${digits.slice(0, 4)}`;
    }
    const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return String(val);
}

function nombrePais(paises, clave) {
    if (!clave) return '—';
    const p = (paises || []).find((x) => x.clave === clave);
    return p ? p.nombre : String(clave);
}

function val(x) {
    const s = String(x || '').trim();
    return s || '—';
}

function labelCriterio(c) {
    if (c === 'propiedad') {
        return 'Propiedad (Titularidad >25% del capital social)';
    }
    if (c === 'control_directo') {
        return 'Control Directo / Voto (Derecho a imponer decisiones o nombrar consejeros)';
    }
    if (c === 'cargo') {
        return 'Cargo (Ejerce la dirección o administración principal)';
    }
    return '—';
}

function domicilioCompleto(d, paises) {
    if (!d) return '—';
    const partes = [
        d.pld_calle,
        d.pld_num_ext ? `No. Ext. ${d.pld_num_ext}` : null,
        d.pld_num_int ? `No. Int. ${d.pld_num_int}` : null,
        d.pld_colonia ? `Col. ${d.pld_colonia}` : null,
        d.pld_ciudad,
        d.pld_estado,
        d.pld_cp ? `C.P. ${d.pld_cp}` : null,
        nombrePais(paises, d.pld_pais_domicilio),
    ].filter((x) => x && String(x).trim() && String(x).trim() !== '—');
    return partes.length ? partes.join(', ') : '—';
}

// ── Paleta (misma que HojaFrontal) ───────────────────────────────────────────
const ROJO_SEC = '#e41b23';
const GRIS = '#555555';
const GRIS_CLARO = '#f8f9fa';
const BORDE = '#cccccc';
const BG_FICHA = '#f0f4f8';

// ── Estilos ──────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
    pagina: {
        paddingTop: 28,
        paddingBottom: 28,
        paddingLeft: 30,
        paddingRight: 30,
        fontFamily: 'Helvetica',
        fontSize: 9,
        color: '#111111',
    },
    header: {
        backgroundColor: GRIS_CLARO,
        paddingTop: 11,
        paddingBottom: 11,
        paddingLeft: 16,
        paddingRight: 16,
        marginBottom: 10,
    },
    headerFila: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    headerTitulo: {
        color: '#000000',
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        letterSpacing: 0.3,
    },
    headerSub: {
        color: '#000000',
        fontSize: 7.5,
        marginTop: 4,
    },
    badgePersona: {
        backgroundColor: '#ffffff',
        color: '#000000',
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 9,
        paddingRight: 9,
        borderRadius: 3,
        borderWidth: 0.5,
        borderColor: BORDE,
    },
    opCard: {
        flexDirection: 'row',
        backgroundColor: BG_FICHA,
        borderWidth: 0.5,
        borderColor: BORDE,
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 10,
        paddingRight: 10,
        marginBottom: 10,
    },
    opCampo: {
        flex: 1,
        paddingRight: 10,
    },
    opEtiqueta: {
        fontSize: 7,
        color: GRIS,
        marginBottom: 2,
    },
    opValor: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
    },
    secHeader: {
        backgroundColor: GRIS_CLARO,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
        paddingRight: 8,
        marginTop: 10,
        marginBottom: 5,
    },
    secTitulo: {
        color: '#000000',
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
    },
    fila: {
        flexDirection: 'row',
        marginBottom: 5,
    },
    campo: {
        flex: 1,
        paddingRight: 8,
    },
    etiqueta: {
        fontSize: 6.8,
        color: GRIS,
        marginBottom: 2,
    },
    valor: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        borderBottomWidth: 0.5,
        borderBottomColor: BORDE,
        paddingBottom: 2,
        minHeight: 12,
    },
    checkRow: {
        marginBottom: 4,
        paddingLeft: 2,
    },
    checkTexto: {
        fontSize: 8,
        color: '#222222',
        lineHeight: 1.35,
    },
    checkMarca: {
        fontFamily: 'Helvetica-Bold',
        color: ROJO_SEC,
    },
    declaracionBox: {
        marginTop: 6,
        marginBottom: 6,
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 8,
        paddingRight: 8,
        borderWidth: 0.5,
        borderColor: BORDE,
        backgroundColor: GRIS_CLARO,
    },
    declaracionTexto: {
        fontSize: 7,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
    },
    articuloBox: {
        marginTop: 10,
        marginBottom: 8,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 8,
        paddingRight: 8,
        borderWidth: 0.5,
        borderColor: BORDE,
        backgroundColor: GRIS_CLARO,
    },
    articuloTitulo: {
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 5,
        color: '#111111',
    },
    articuloTexto: {
        fontSize: 6.5,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
        marginBottom: 4,
    },
    articuloInciso: {
        fontSize: 6.5,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
        marginBottom: 3,
        paddingLeft: 8,
    },
    articuloSubinciso: {
        fontSize: 6.5,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
        marginBottom: 2,
        paddingLeft: 16,
    },
    docsAnexo: {
        fontSize: 6.5,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
        marginTop: 4,
    },
    footer: {
        marginTop: 16,
        paddingTop: 8,
        borderTopWidth: 0.5,
        borderTopColor: BORDE,
    },
    firmaFila: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 28,
    },
    firmaBloque: {
        width: '55%',
        borderTopWidth: 0.8,
        borderTopColor: '#333333',
        paddingTop: 4,
    },
    firmaLabel: {
        fontSize: 7,
        color: GRIS,
        textAlign: 'center',
    },
    nota: {
        fontSize: 6.5,
        color: '#999999',
        textAlign: 'center',
        marginTop: 10,
    },
});

function Seccion({ letra, titulo }) {
    return (
        <View style={S.secHeader}>
            <Text style={S.secTitulo}>
                {letra}.  {titulo}
            </Text>
        </View>
    );
}

function Fila({ children }) {
    return <View style={S.fila}>{children}</View>;
}

function Campo({ label, value, flex = 1 }) {
    return (
        <View style={[S.campo, { flex }]}>
            <Text style={S.etiqueta}>{label}</Text>
            <Text style={S.valor}>{val(value)}</Text>
        </View>
    );
}

function CheckCriterio({ activo, texto }) {
    return (
        <View style={S.checkRow}>
            <Text style={S.checkTexto}>
                <Text style={S.checkMarca}>{activo ? '( X ) ' : '(   ) '}</Text>
                {texto}
            </Text>
        </View>
    );
}

/**
 * Constancia de Identificación de Beneficiario Controlador (BC).
 * @param {object}   props.operacion
 * @param {object}   props.sujetoDetalle  Sujeto BC (persona física) con campos pld_* / pld_bc_*
 * @param {string}   [props.origenBc]
 * @param {Array}    [props.paises]
 */
export default function HojaFormatoBC({ operacion, sujetoDetalle: d, origenBc, paises }) {
    const nombreCompleto = d
        ? [d.nombre, d.paterno, d.materno].filter(Boolean).join(' ')
        : '—';
    const criterio = (d && d.pld_bc_criterio_control) || '';
    const empresa =
        (d && d.pld_bc_empresa_cliente) ||
        (operacion && operacion.denominacion) ||
        '—';
    const denom = (operacion && operacion.denominacion) || '—';

    return (
        <Document>
            <Page size="A4" style={S.pagina}>
                <View style={S.header}>
                    <View style={S.headerFila}>
                        <View>
                            <Text style={S.headerTitulo}>
                                CONSTANCIA DE IDENTIFICACIÓN DE BENEFICIARIO CONTROLADOR (BC)
                            </Text>
                            <Text style={S.headerSub}>
                                Prevención de Lavado de Dinero  ·  Formato de Beneficiario Controlador
                            </Text>
                        </View>
                        <Text style={S.badgePersona}>PERSONA FÍSICA</Text>
                    </View>
                </View>

                <View style={S.opCard}>
                    <View style={S.opCampo}>
                        <Text style={S.opEtiqueta}>Denominación del expediente</Text>
                        <Text style={S.opValor}>{denom}</Text>
                    </View>
                    <View style={S.opCampo}>
                        <Text style={S.opEtiqueta}>Origen BC</Text>
                        <Text style={S.opValor}>{val(origenBc) !== '—' ? origenBc : labelCriterio(criterio)}</Text>
                    </View>
                </View>

                <Seccion letra="I" titulo="RELACIÓN CON LA PERSONA MORAL (CLIENTE)" />
                <Fila>
                    <Campo label="Empresa / Cliente" value={empresa} flex={2} />
                </Fila>
                <Text style={[S.etiqueta, { marginBottom: 4 }]}>Criterio de control:</Text>
                <CheckCriterio
                    activo={criterio === 'propiedad'}
                    texto="Propiedad (Titularidad >25% del capital social)"
                />
                <CheckCriterio
                    activo={criterio === 'control_directo'}
                    texto="Control Directo / Voto (Derecho a imponer decisiones o nombrar consejeros)"
                />
                <CheckCriterio
                    activo={criterio === 'cargo'}
                    texto="Cargo (Ejerce la dirección o administración principal)"
                />

                <Seccion
                    letra="II"
                    titulo="DATOS PERSONALES DEL BENEFICIARIO CONTROLADOR (PERSONA FÍSICA)"
                />
                <Fila>
                    <Campo label="Nombre(s)" value={d && d.nombre} flex={2} />
                    <Campo label="Apellido paterno" value={d && d.paterno} />
                    <Campo label="Apellido materno" value={d && d.materno} />
                </Fila>
                <Fila>
                    <Campo label="RFC (con homoclave)" value={d && d.rfc} />
                    <Campo label="CURP" value={d && d.curp} flex={1.2} />
                </Fila>
                <Fila>
                    <Campo
                        label="Fecha de nacimiento"
                        value={fmtFecha(d && d.pld_fecha_nac_const)}
                    />
                    <Campo
                        label="País de nacimiento"
                        value={nombrePais(paises, d && d.pld_pais_nacimiento)}
                    />
                    <Campo label="Nacionalidad" value={d && d.pld_nacionalidad} />
                </Fila>
                <Fila>
                    <Campo
                        label="Actividad económica u ocupación"
                        value={
                            (d && d.pld_ocupacion) ||
                            (d && d.pld_actividad_giro_descripcion)
                        }
                        flex={2}
                    />
                </Fila>
                <Fila>
                    <Campo
                        label="Domicilio particular"
                        value={domicilioCompleto(d, paises)}
                        flex={2}
                    />
                </Fila>
                <Fila>
                    <Campo label="Teléfono" value={d && d.pld_telefono} />
                    <Campo
                        label="Correo electrónico"
                        value={d && d.pld_correo}
                        flex={1.5}
                    />
                </Fila>

                <Seccion
                    letra="III"
                    titulo="DECLARACIÓN Y FIRMA DE CONFORMIDAD DEL CLIENTE / REPRESENTANTE"
                />
                <View style={S.declaracionBox}>
                    <Text style={S.declaracionTexto}>
                        El que suscribe, en mi carácter de Cliente / Representante Legal o
                        Apoderado, declaro bajo protesta de decir verdad que se me ha requerido
                        la información sobre la existencia de un Dueño Beneficiario /
                        Beneficiario Controlador, y manifiesto que los datos aquí proporcionados
                        sobre su identidad son correctos y veraces.
                    </Text>
                </View>
                <Fila>
                    <Campo
                        label="Nombre del Cliente / Representante Legal o Apoderado"
                        value={d && d.pld_bc_declarante_nombre}
                        flex={2}
                    />
                    <Campo
                        label="Fecha"
                        value={fmtFecha(d && d.pld_bc_fecha_declaracion)}
                    />
                </Fila>
                <Text style={[S.etiqueta, { marginTop: 4 }]}>
                    Beneficiario Controlador identificado: {val(nombreCompleto)}
                </Text>

                <View style={S.articuloBox}>
                    <Text style={S.articuloTitulo}>
                        Fundamento Legal: Artículo 3 de la LFPIORPI
                    </Text>
                    <Text style={S.articuloTexto}>
                        Para los efectos de esta Ley, se entenderá por: III. Beneficiario
                        Controlador, a la persona física o grupo de personas físicas que:
                    </Text>
                    <Text style={S.articuloInciso}>
                        a) Directamente o por medio de alguna persona Cliente o Usuaria obtiene,
                        en última instancia, el beneficio de goce, uso, disfrute,
                        aprovechamiento o disposición del bien o servicio derivado de la
                        realización de un acto u operación con quien realice una Actividad
                        Vulnerable, o
                    </Text>
                    <Text style={S.articuloInciso}>
                        b) Ejerce el control efectivo en última instancia de aquella persona
                        moral que, en su carácter de Cliente o Usuaria, lleve a cabo actos u
                        operaciones con quien realice una Actividad Vulnerable, así como las
                        personas por cuenta de quienes celebra alguno de ellos.
                    </Text>
                    <Text style={S.articuloTexto}>
                        Se entiende que una persona o grupo de personas controla de manera
                        efectiva en última instancia a una persona moral cuando, a través de la
                        titularidad de valores, por contrato o cualquier otro acto, puede:
                    </Text>
                    <Text style={S.articuloSubinciso}>
                        i) Imponer, directa o indirectamente, decisiones en las asambleas
                        generales de accionistas, socios u órganos equivalentes, o nombrar o
                        destituir a la mayoría de los consejeros, administradores o sus
                        equivalentes;
                    </Text>
                    <Text style={S.articuloSubinciso}>
                        ii) Mantener la titularidad de los derechos que permitan, directa o
                        indirectamente, ejercer el voto respecto de más del veinticinco por
                        ciento del capital social, o
                    </Text>
                    <Text style={S.articuloSubinciso}>
                        iii) Dirigir, directa o indirectamente, la administración, la estrategia
                        o las principales políticas de la misma.
                    </Text>
                    <Text style={[S.articuloTexto, { marginBottom: 0 }]}>
                        (Nota: La definición de Beneficiario Controlador es equiparable a
                        beneficiario final y propietario real).
                    </Text>
                    <Text style={S.docsAnexo}>
                        Documentos del Beneficiario Controlador (Persona Física) que se deben
                        anexar a este formato: (i) Copia de Identificación Oficial vigente (INE
                        o Pasaporte / Documento migratorio si es extranjero). (ii) Copia de
                        Comprobante de Domicilio particular (antigüedad no mayor a tres meses).
                        (iii) Copia de CURP y RFC (Cédula de Identificación Fiscal), cuando
                        cuente con ellos.
                    </Text>
                </View>

                <View style={S.footer}>
                    <View style={S.firmaFila}>
                        <View style={S.firmaBloque}>
                            <Text style={S.firmaLabel}>
                                Nombre y Firma del Cliente / Representante Legal o Apoderado
                            </Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
}
